// ============ SUPER TD · สถิติรายสินทรัพย์ (engine v0.3.0) ============
// The same card the NAS100 panel gives you — live EET clock, ON/OFF window with
// countdown, live quote, today's verdict, next sessions, edge ticks with a
// countdown to the nearest one, and the monthly heat strip — but driven by the
// statistics engine and available for all eight instruments.
//
// What the engine adds over the hand-built NAS100 schedule is honesty about
// sample size, and those rules are load-bearing, not decoration:
//   • no percentage is ever shown without its n — 27% on 11 observations and
//     56% on 2,669 look identical otherwise
//   • a rate with usable=false renders greyed with no tick, never a confident
//     number
//   • the monthly strip is judged on the 95% CI (lo > 50 / hi < 50), because
//     `significant` demands n>=30 and a 21-year strip can never reach it; only
//     16 of 96 monthly cells across all eight assets actually clear it
//   • excluded hours, warnings and the disclaimer are surfaced, not hidden
import React, { useEffect, useMemo, useState } from 'react';
import { SecHead } from './core';
import { useT, useLang } from '../lib/i18n';
import { INDICES, ASSETS } from '../data';

interface Rate {
  n: number; wins: number; pct: number; lo: number; hi: number;
  p: number; edge: number; significant: boolean; usable: boolean; green: boolean;
  labelTh?: string; labelEn?: string; allYear?: Rate;
}
interface Slot { time: string; timeframe: string; pct: number; n: number; ci?: [number, number]; focusPct?: number; focusN?: number; }
interface Win {
  timeframe: string; start: string; end: string; range: string; pct: number; n: number;
  peak: { time: string; pct: number; n: number; focusPct?: number; focusN?: number };
}
interface Session { date: string; labelTh: string; labelEn: string; score: number; factors?: Record<string, number>; }
interface MonthCell extends Rate { month: number; labelTh: string; labelEn: string; }
interface Card {
  asset: string; label: string; kind: string; asOf: string; serverTimezone: string;
  weekScheme?: string; monthlyBasis?: string; excludedHours?: number[];
  // Note the shape differs from index.json, where focusMonths is a plain
  // string array — inside a card it carries both label sets and whether the
  // months were derived rather than declared.
  focusMonths?: { months: number[]; labelsTh: string[]; labelsEn: string[]; derived?: boolean };
  // Baked on the engine's own build date — `dayOfMonth.value` is which day
  // that was, and the only thing this block is still trusted for (see `flags`).
  today?: { dayOfMonth?: Rate & { value?: number }; weekday?: Rate; weekOfMonth?: Rate; month?: Rate };
  sessions?: Session[]; windows?: Win[];
  slots?: { green?: Slot[]; red?: Slot[] };
  monthly?: Record<string, MonthCell[]>;
  weekTable?: Record<string, Rate>;                      // engine's own, kept for reference
  // One bucket per lookback, added by research/supertd/build_weekday_by_week.py.
  // Everything the three calendar strips render comes from here, so a span
  // change moves all of them together.
  byYears?: Record<string, {
    monthly?: MonthCell[];
    week?: Record<string, Rate>;
    weekday?: Record<string, Rate>;
    weekdayByWeek?: Record<string, Record<string, Rate>>;
  }>;
  weekdayTable?: Record<string, Record<string, Rate>>;    // allYear | thisMonth | focusMonths → Mon…Fri
  coverage?: { daily?: { years: number; from: string }; intraday?: { byTimeframe?: Record<string, { years: number; native: boolean }> } };
  dataSource?: string; disclaimer?: string; warnings?: { level: string; field: string; message: string }[];
}
interface IndexAsset { key: string; label: string; kind: string; }
interface Index { assets?: IndexAsset[]; sources?: Record<string, string>; }

// Which of our live rows carries this instrument's price. FX crosses have no
// row in the app's asset list, so those cards simply run without a quote.
const QUOTE: Record<string, { from: 'index' | 'asset'; sym: string }> = {
  NAS100: { from: 'index', sym: 'NDX' },
  US500: { from: 'index', sym: 'SPX' },
  US30: { from: 'index', sym: 'DJI' },
  XAUUSD: { from: 'asset', sym: 'GOLD' },
  BTCUSD: { from: 'asset', sym: 'BTC' },
  USOIL: { from: 'asset', sym: 'WTI' },
};

const MONTH_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const MONTH_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Wall-clock parts in the EA server timezone (EET, DST-aware). */
function serverNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Bucharest', hour12: false,
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    year: 'numeric', day: '2-digit', month: '2-digit', weekday: 'short',
  }).formatToParts(new Date());
  const get = (k: string) => parts.find(p => p.type === k)?.value || '';
  return {
    hms: `${get('hour')}:${get('minute')}:${get('second')}`,
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
    day: Number(get('day')),
    month: Number(get('month')),
    weekday: get('weekday'), // 'Mon' … 'Sun'
    // Server-side date, for comparing against the engine's session dates.
    iso: `${get('year')}-${get('month')}-${get('day')}`,
  };
}

const WD_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const WD_TH: Record<string, string> = { Mon: 'จันทร์', Tue: 'อังคาร', Wed: 'พุธ', Thu: 'พฤหัส', Fri: 'ศุกร์' };
const WD_TH_SHORT: Record<string, string> = { Mon: 'จ', Tue: 'อ', Wed: 'พ', Thu: 'พฤ', Fri: 'ศ' };
/** Which W-block a day-of-month falls in, matching the engine's `dom` scheme. */
const weekBlock = (day: number) => (day <= 7 ? 'W1' : day <= 14 ? 'W2' : day <= 21 ? 'W3' : day <= 28 ? 'W4' : 'W5');
const toMin = (hm: string) => { const [h, m] = hm.split(':').map(Number); return h * 60 + m; };
const fmtLeft = (min: number, t: (a: string, b: string) => string) =>
  min >= 60 ? `${Math.floor(min / 60)} ${t('ชม.', 'h')} ${min % 60} ${t('นาที', 'm')}` : `${min} ${t('นาที', 'm')}`;
const pctText = (v?: number) => (typeof v === 'number' ? `${v.toFixed(1)}%` : '—');
const nText = (n?: number) => (typeof n === 'number' ? `n=${n.toLocaleString()}` : '');

/** The honest test for a small sample: does the interval clear 50 either way? */
function ciVerdict(r?: Rate): 'up' | 'down' | 'flat' {
  if (!r || !r.usable) return 'flat';
  if (r.lo > 50) return 'up';
  if (r.hi < 50) return 'down';
  return 'flat';
}
const mark = (v: 'up' | 'down' | 'flat') => (v === 'up' ? '✓' : v === 'down' ? '✗' : '–');

export function SuperTdStats() {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [idx, setIdx] = useState<Index | null>(null);
  const [asset, setAsset] = useState('NAS100');
  const [card, setCard] = useState<Card | null>(null);
  const [cache] = useState<Map<string, Card>>(() => new Map());
  // The one lookback everything reads from. Opens on the instrument's FULL
  // history (user's call, 11 Aug 2026): for NAS100 that is 40.8 years back to
  // Oct 1985 — the "41 ปี" figure — and every other asset gets its own depth
  // rather than a 20-year default that quietly threw away half the record.
  const [span, setSpan] = useState('all');
  const [dayCut, setDayCut] = useState('all');  // weekday across the span, or only within this week block
  const [now, setNow] = useState(serverNow());
  const [tick, setTick] = useState(0);
  const [failed, setFailed] = useState(false);
  // The EA's real schedule — the same super_td.json the 24h controller and the
  // daily Discord alert read. This card used to badge its own strongest
  // STATISTICAL window as "EA ON", which at 07:25 EET read ON while the EA was
  // actually off: the engine ranks every hour, the controller only trades the
  // US session. The badge now answers the question it asks.
  const [ea, setEa] = useState<any>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/supertd/index')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => { if (alive) setIdx(v); })
      .catch(() => { if (alive) setFailed(true); });
    fetch('/api/supertd')
      .then(r => (r.ok ? r.json() : null))
      .then(v => { if (alive) setEa(v); })
      .catch(() => {});
    const clock = setInterval(() => setNow(serverNow()), 1000);
    const onLive = () => setTick(x => x + 1);
    window.addEventListener('mm-live-tick', onLive);
    return () => { alive = false; clearInterval(clock); window.removeEventListener('mm-live-tick', onLive); };
  }, []);

  useEffect(() => {
    let alive = true;
    const hit = cache.get(asset);
    if (hit) { setCard(hit); return; }
    setCard(null);
    fetch(`/api/supertd/card?asset=${encodeURIComponent(asset)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => { if (alive) { cache.set(asset, v); setCard(v); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [asset, cache]);

  // Live quote for the selected instrument — the live engine mutates these rows
  // in place, so re-read on every tick rather than holding a copy.
  const quote = useMemo(() => {
    const q = QUOTE[asset];
    if (!q) return null;
    const row: any = q.from === 'index' ? INDICES.find(x => x.sym === q.sym) : ASSETS.find(x => x.sym === q.sym);
    return row ? { price: row.price as number, chg: row.chg as number } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, tick]);

  // ON/OFF against the engine's strongest windows, with a live countdown. These
  // windows are where the edge sits, so "ON" means inside one of them.
  const status = useMemo(() => {
    const wins = (card?.windows || []).slice().sort((a, b) => toMin(a.start) - toMin(b.start));
    if (!wins.length) return null;
    const m = now.minutes;
    const inside = wins.find(w => m >= toMin(w.start) && m < toMin(w.end));
    if (inside) return { on: true, until: toMin(inside.end) - m, window: inside };
    const ahead = wins.find(w => toMin(w.start) > m);
    return ahead
      ? { on: false, until: toMin(ahead.start) - m, window: ahead }
      : { on: false, until: (24 * 60 - m) + toMin(wins[0].start), window: wins[0] };
  }, [card, now.minutes]);

  // EA ON/OFF straight from the controller's own window list, by the identical
  // rule the seasonality card uses, so the two cards on this page can never
  // disagree about whether the EA is trading. The schedule is NAS100-only —
  // every other tab keeps the engine's window, labelled as what it is.
  const eaStatus = useMemo(() => {
    const wins: any[] = (asset === 'NAS100' && ea?.eaWindows?.on) || [];
    if (!wins.length) return null;
    const m = now.minutes;
    const inside = wins.find(w => m >= toMin(w.start) && m < toMin(w.end));
    if (inside) return { on: true, until: toMin(inside.end) - m, w: inside };
    const next = wins.map(w => ({ w, at: toMin(w.start) })).filter(x => x.at > m).sort((a, b) => a.at - b.at)[0];
    return next
      ? { on: false, until: next.at - m, w: next.w }
      : { on: false, until: (24 * 60 - m) + toMin(wins[0].start), w: wins[0] };
  }, [asset, ea, now.minutes]);

  // Nearest upcoming edge tick, green or red, rolling over to tomorrow.
  const nextTick = useMemo(() => {
    const all = [
      ...(card?.slots?.green || []).map(s => ({ s, kind: 'green' as const })),
      ...(card?.slots?.red || []).map(s => ({ s, kind: 'red' as const })),
    ].map(x => ({ ...x, at: toMin(x.s.time) })).sort((a, b) => a.at - b.at);
    if (!all.length) return null;
    const ahead = all.find(x => x.at > now.minutes);
    return ahead ? { ...ahead, left: ahead.at - now.minutes } : { ...all[0], left: (24 * 60 - now.minutes) + all[0].at };
  }, [card, now.minutes]);

  // Not every instrument offers every span — BTCUSD has 11.9 years of daily
  // history and so has no 20Y or 40Y. Fall back to the deepest one it does
  // have rather than rendering empty strips. Computed here, above the early
  // returns, because the TODAY row reads the same bucket the strips do.
  const { spanKey, bucket } = useMemo(() => {
    const keys = Object.keys(card?.byYears || {});
    const k = card?.byYears?.[span] ? span : (card?.byYears?.['all'] ? 'all' : keys[keys.length - 1]) || '';
    return { spanKey: k, bucket: card?.byYears?.[k] || {} };
  }, [card, span]);

  // TODAY, derived from the live server date — never from the card's baked
  // `today` block. The per-asset cards are rebuilt by hand (all nine are dated
  // 31 Jul), so that block is frozen on the day the engine last ran: on 11 Aug
  // it still read Wed / ก.ค., and the day-of-month tile printed the 29th's
  // sample (53.0%, n=164) under *today's* date — one day's statistics wearing
  // another day's label. Reading the same per-span buckets the strips below
  // use keeps the verdict true to the date and moves it with the lookback.
  const flags = useMemo(() => {
    const wk = Object.entries(bucket.week || {}).find(([k]) => k.startsWith(weekBlock(now.day)));
    const mo = bucket.monthly?.find(m => m.month === now.month);
    // The engine bakes a single day-of-month sample: its own build date. Keep
    // that tile only when the date IS today — an absent tile beats a wrong one,
    // and there is no per-day table in the card to compute the rest from.
    const dom = card?.today?.dayOfMonth;
    return [
      { key: 'dom', label: `${t('วันที่', 'Day')} ${now.day}`, r: dom?.value === now.day ? dom : undefined },
      { key: 'wd', label: en ? now.weekday : (WD_TH[now.weekday] || now.weekday), r: bucket.weekday?.[now.weekday] },
      { key: 'wom', label: `${t('สัปดาห์', 'Week')} ${wk ? wk[0].split(' ')[0] : ''}`.trim(), r: wk?.[1] },
      { key: 'mo', label: mo ? (en ? mo.labelEn : mo.labelTh) : '', r: mo },
    ].filter(x => x.r);
  }, [card, bucket, now.day, now.month, now.weekday, t, en]);

  if (failed) return null;
  if (!idx || !card || !status) {
    return (
      <div className="mm-card glass mm-supertd mm-stds">
        <SecHead th="Super Cycle Time & Duration · สถิติรายสินทรัพย์" en="SUPER CYCLE TIME & DURATION · PER-ASSET STATISTICS"></SecHead>
        <p className="dim" style={{ padding: '8px 2px' }}>{t('กำลังโหลดสถิติ…', 'Loading statistics…')}</p>
      </div>
    );
  }

  const tf = card.coverage?.intraday?.byTimeframe || {};
  const spanKeys = Object.keys(card.byYears || {});   // the lookback buttons
  // Whole-history depth in years, for the ALL button's label (40.8 → 41Y).
  const fullYears = card.coverage?.daily?.years ? Math.round(card.coverage.daily.years) : 0;
  // Sessions the engine scored ahead of ITS build date. Drop the ones that
  // have since gone by, so a hand-rebuilt card cannot advertise last month's
  // dates under "next sessions" (ISO strings, so a plain compare is enough).
  const sessions = (card.sessions || []).filter(s => s.date >= now.iso);
  // Hue follows the value so a cell never reads green while showing 25%.
  // Strength follows confidence: full shade only when the 95% interval actually
  // cleared 50, a wash when it did not, and a fainter wash again when the
  // sample never reached the minimum. Colour therefore says *which way*, and
  // depth says *how much to trust it* — the two questions a reader has.
  const heatOf = (r: Rate) => {
    const v = ciVerdict(r); // 'flat' whenever the sample is unusable
    if (v === 'up') return 'rgba(0,224,176,0.55)';
    if (v === 'down') return 'rgba(255,59,92,0.35)';
    const a = r.usable ? 0.2 : 0.11;
    if (r.pct >= 55) return `rgba(0,224,176,${a})`;
    if (r.pct <= 45) return `rgba(255,59,92,${a})`;
    return 'rgba(255,255,255,0.06)';
  };
  const fm = card.focusMonths;
  const focus = fm ? (en ? fm.labelsEn : fm.labelsTh).join('/') : '';

  return (
    <div className="mm-card glass mm-supertd mm-stds">
      <div className="mm-guru-head">
        <SecHead th={`Super Cycle Time & Duration · ${card.label}`} en={`SUPER CYCLE TIME & DURATION · ${card.label} STATISTICS`}></SecHead>
        <div className="mm-guru-tabs">
          {(idx.assets || []).map(a => (
            <button key={a.key} className={`mm-pill sm ${asset === a.key ? 'on' : ''}`} onClick={() => setAsset(a.key)}>{a.label}</button>
          ))}
        </div>
      </div>

      <div className="mm-std-top">
        {eaStatus ? (
          // The real thing: the controller's own ON windows.
          <div className={`mm-std-status ${eaStatus.on ? 'on' : 'off'}`}>
            <b className="mono">{eaStatus.on ? 'EA ON' : 'EA OFF'}</b>
            <span>{eaStatus.on
              ? `${t('อยู่ในช่วง', 'inside')} ${eaStatus.w.start}–${eaStatus.w.end} · ${t('ปิดใน', 'closes in')} ${fmtLeft(eaStatus.until, t)}${eaStatus.w.reason ? ` · ${eaStatus.w.reason}` : ''}`
              : `${t('หน้าต่างถัดไป', 'next window')} ${eaStatus.w.start} (${fmtLeft(eaStatus.until, t)})`}</span>
          </div>
        ) : (
          // No EA schedule for this instrument — say "strong window", not "EA".
          <div className={`mm-std-status ${status.on ? 'on' : 'off'}`}>
            <b className="mono">{status.on ? t('ช่วงเด่น', 'STRONG') : t('นอกช่วงเด่น', 'QUIET')}</b>
            <span>{status.on
              ? `${t('อยู่ในช่วง', 'inside')} ${status.window.range} · ${t('ปิดใน', 'closes in')} ${fmtLeft(status.until, t)} · ${pctText(status.window.pct)} (${nText(status.window.n)})`
              : `${t('หน้าต่างถัดไป', 'next window')} ${status.window.start} (${fmtLeft(status.until, t)})`}</span>
          </div>
        )}
        {quote && (
          <div className={`mm-std-quote mono ${quote.chg >= 0 ? 'up' : 'down'}`}>
            <span className="dim">{card.label}</span>
            <b>{quote.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}</b>
            <em>{quote.chg >= 0 ? '▲' : '▼'} {quote.chg >= 0 ? '+' : ''}{quote.chg.toFixed(2)}%</em>
          </div>
        )}
        <div className="mm-std-clock mono">
          <b>{now.hms}</b>
          <span className="dim">{t('เวลา Server (EET)', 'Server time (EET)')}</span>
        </div>
      </div>

      {/* Today — the ✓/✗ chip carries the verdict, the tile under it carries the evidence */}
      <div className="mm-std-verdict">
        <span className="mm-std-lbl mono">{t('วันนี้', 'TODAY')}</span>
        {flags.map(f => {
          const v = ciVerdict(f.r);
          return (
            <span key={f.key} className={`mm-std-chip ${f.r!.usable ? (v === 'flat' ? 'dim' : v) : 'dim'}`}
              title={f.r!.usable ? `${pctText(f.r!.pct)} · ${nText(f.r!.n)} · 95% CI ${f.r!.lo.toFixed(1)}–${f.r!.hi.toFixed(1)}%` : t('ตัวอย่างน้อยเกินไป', 'sample too small')}>
              {f.label} <b>{f.r!.usable ? mark(v) : '·'}</b>
            </span>
          );
        })}
      </div>

      <div className="mm-stds-flags">
        {flags.map(f => {
          const r = f.r!;
          const v = ciVerdict(r);
          return (
            <div key={f.key} className={`mm-stds-flag ${r.usable ? v : 'off'}`}>
              <span className="dim">{f.label}</span>
              {r.usable
                ? <React.Fragment>
                    <b className="mono">{pctText(r.pct)}</b>
                    <em className="mono dim">{nText(r.n)} · CI {r.lo.toFixed(0)}–{r.hi.toFixed(0)}
                      {r.allYear ? ` · ${t('ทั้งปี', 'all-yr')} ${pctText(r.allYear.pct)}` : ''}</em>
                  </React.Fragment>
                : <React.Fragment>
                    <b className="mono dim">—</b>
                    <em className="mono dim">{t('ตัวอย่างน้อย', 'thin sample')} {nText(r.n)}</em>
                  </React.Fragment>}
            </div>
          );
        })}
      </div>

      {!!sessions.length && (
        <div className="mm-std-verdict">
          <span className="mm-std-lbl mono">{t('เซสชันถัดไป', 'NEXT SESSIONS')}</span>
          {sessions.map(s => (
            <span key={s.date} className={`mm-std-chip ${s.score > 0 ? 'up' : s.score < 0 ? 'down' : 'dim'}`}
              title={s.factors ? Object.entries(s.factors).map(([k, v]) => `${k} ${v > 0 ? '+' : ''}${v}`).join(' · ') : ''}>
              {en ? s.labelEn : s.labelTh} <b>{s.score > 0 ? `+${s.score}` : s.score}</b>
            </span>
          ))}
        </div>
      )}

      <div className="mm-std-windows">
        {(card.windows || []).map((w, i) => (
          <div className="mm-std-win" key={i}>
            <span className="mono up">▶ {w.start}–{w.end}</span>
            <span className="dim">
              {w.timeframe} {t('พีค', 'peak')} {w.peak.time} — {pctText(w.peak.pct)} ({nText(w.peak.n)})
              {typeof w.peak.focusPct === 'number' && focus ? ` · ${focus} ${pctText(w.peak.focusPct)} (${nText(w.peak.focusN)})` : ''}
              {` · ${t('ทั้งช่วง', 'window')} ${pctText(w.pct)} (${nText(w.n)})`}
            </span>
          </div>
        ))}
      </div>

      <div className="mm-std-ticks">
        {nextTick && (
          <span className={`mm-std-tick next ${nextTick.kind === 'green' ? 'up' : 'down'} mono`}
            title={t('จุดสัญญาณ intraday ที่ใกล้ที่สุด', 'Nearest upcoming intraday edge tick')}>
            ⏭ {nextTick.kind === 'green' ? '🟢' : '🔴'} {nextTick.s.time} · {fmtLeft(nextTick.left, t)}
          </span>
        )}
        {(card.slots?.green || []).map(s => (
          <span key={`g${s.time}${s.timeframe}`} className="mm-std-tick up mono"
            title={s.ci ? `95% CI ${s.ci[0].toFixed(1)}–${s.ci[1].toFixed(1)}%` : ''}>
            🟢 {s.time} <em className="dim">{s.timeframe}</em> {pctText(s.pct)} <i className="dim">{nText(s.n)}</i>
            {typeof s.focusPct === 'number' && focus ? <i className="dim"> · {focus} {pctText(s.focusPct)}</i> : null}
          </span>
        ))}
        {(card.slots?.red || []).map(s => (
          <span key={`r${s.time}${s.timeframe}`} className="mm-std-tick down mono"
            title={s.ci ? `95% CI ${s.ci[0].toFixed(1)}–${s.ci[1].toFixed(1)}%` : ''}>
            🔴 {s.time} <em className="dim">{s.timeframe}</em> {pctText(s.pct)} <i className="dim">{nText(s.n)}</i>
          </span>
        ))}
      </div>

      {/* One span drives everything below it. The month row is the whole
          window, the week row splits that window by week-of-month, and the day
          row splits it again by weekday — so the three read as one decomposition
          instead of three tables that happened to be counted differently. */}
      <div className="mm-std-verdict mm-stds-span">
        <span className="mm-std-lbl mono">{t('ช่วงสถิติย้อนหลัง', 'LOOKBACK')}</span>
        {spanKeys.map(k => (
          <button key={k} className={`mm-std-winbtn ${spanKey === k ? 'on' : ''}`} onClick={() => setSpan(k)}
            title={k === 'all' && card.coverage?.daily?.from
              ? t(`ทั้งหมด · ตั้งแต่ ${card.coverage.daily.from}`, `Full history · since ${card.coverage.daily.from}`) : ''}>
            {/* "ALL" hid how much history that actually is — 41 years for
                NAS100, 12 for BTCUSD. Name the depth so the reader knows what
                the numbers rest on. */}
            {k === 'all' ? (fullYears ? `${fullYears}Y` : t('ทั้งหมด', 'ALL')) : `${k}Y`}
          </button>
        ))}
        <span className="mm-footnote dim">
          {t('เลือกช่วงเดียว — เดือน สัปดาห์ และวัน เปลี่ยนตามพร้อมกันทั้งหมด', 'one span — the month, week and day rows all move with it')}
        </span>
      </div>

      {/* 1 — the year */}
      {!!bucket.monthly?.length && (
        <div className="mm-std-months">
          <span className="mm-std-lbl mono">{t('รายเดือน · โอกาสปิดเหนือเดือนก่อน', 'BY MONTH · CLOSE ABOVE PREVIOUS MONTH')}</span>
          <div className="mm-std-heat">
            {bucket.monthly.map(m => (
              <span key={m.month} className={`mm-std-cell ${m.month === now.month ? 'now' : ''} ${m.usable ? '' : 'off'}`}
                style={{ background: heatOf(m) }}
                title={`${en ? m.labelEn : m.labelTh} · ${pctText(m.pct)} · ${nText(m.n)} · 95% CI ${m.lo.toFixed(1)}–${m.hi.toFixed(1)}%`}>
                <em>{en ? m.labelEn : m.labelTh}</em>
                <b className="mono">{Math.round(m.pct)}</b>
                <i className="mono dim">{m.n}</i>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 2 — the week */}
      {!!bucket.week && (
        <div className="mm-std-months">
          <span className="mm-std-lbl mono">{t('รายสัปดาห์ในเดือน', 'BY WEEK OF MONTH')}</span>
          <div className="mm-std-heat">
            {Object.entries(bucket.week).map(([label, r]) => (
              <span key={label} className={`mm-std-cell ${label.split(' ')[0] === weekBlock(now.day) ? 'now' : ''} ${r.usable ? '' : 'off'}`}
                style={{ background: heatOf(r) }}
                title={`${label} · ${pctText(r.pct)} · ${nText(r.n)} · 95% CI ${r.lo.toFixed(1)}–${r.hi.toFixed(1)}%`}>
                <em>{label.replace(' · ', ' ')}</em>
                <b className="mono">{Math.round(r.pct)}</b>
                <i className="mono dim">{r.n}</i>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 3 — the day, either across the span or narrowed to the week block we
          are actually in, which is the finer cut of the same window. */}
      {!!bucket.weekday && (
        <div className="mm-std-months">
          <span className="mm-std-lbl mono">
            {t('รายวัน จันทร์–ศุกร์', 'BY WEEKDAY, MON–FRI')}
            <button className={`mm-std-winbtn ${dayCut === 'all' ? 'on' : ''}`} onClick={() => setDayCut('all')}>{t('ทุกสัปดาห์', 'any week')}</button>
            <button className={`mm-std-winbtn ${dayCut === 'week' ? 'on' : ''}`} onClick={() => setDayCut('week')}>{t('เฉพาะ', 'only ') + weekBlock(now.day)}</button>
          </span>
          <div className="mm-std-heat">
            {WD_KEYS.map(k => {
              const r = dayCut === 'week'
                ? bucket.weekdayByWeek?.[weekBlock(now.day)]?.[k]
                : bucket.weekday?.[k];
              if (!r) return (
                <span key={k} className="mm-std-cell off" title={t('ไม่มีข้อมูลในช่วงนี้', 'no data in this span')}>
                  <em>{en ? k : WD_TH[k]}</em><b className="mono">—</b><i className="mono dim">·</i>
                </span>
              );
              return (
                <span key={k} className={`mm-std-cell ${k === now.weekday ? 'now' : ''} ${r.usable ? '' : 'off'}`}
                  style={{ background: heatOf(r) }}
                  title={`${k}${dayCut === 'week' ? ` · ${weekBlock(now.day)}` : ''} · ${pctText(r.pct)} · ${nText(r.n)} · 95% CI ${r.lo.toFixed(1)}–${r.hi.toFixed(1)}%`}>
                  <em>{en ? k : WD_TH[k]}</em>
                  <b className="mono">{Math.round(r.pct)}</b>
                  <i className="mono dim">{r.n}</i>
                </span>
              );
            })}
          </div>
        </div>
      )}


      <div className="mm-mlearn-meta mono dim">
        <span>{t('ข้อมูลถึง', 'as of')} {card.asOf}</span>
        <span>· {t('รายวัน', 'daily')} {card.coverage?.daily?.years?.toFixed(1)}y</span>
        {Object.entries(tf).map(([k, v]) => <span key={k}>· {k} {v.years.toFixed(1)}y{v.native ? '' : '*'}</span>)}
        {focus ? <span title={fm?.derived ? t('เดือนโฟกัสมาจากการคำนวณ ไม่ได้กำหนดเอง', 'focus months were derived, not declared') : ''}>· {t('เดือนโฟกัส', 'focus')} {focus}{fm?.derived ? '*' : ''}</span> : null}
      </div>

      {!!card.warnings?.length && (
        <ul className="mm-stds-warn">
          {card.warnings.map((w, i) => <li key={i} className={w.level === 'caution' ? 'down' : 'dim'}>{w.level === 'caution' ? '⚠' : 'ℹ'} {w.message}</li>)}
        </ul>
      )}

      <p className="mm-footnote dim">
        {card.dataSource ? `${card.dataSource} · ` : ''}
        {card.excludedHours?.length ? `${t('ตัดชั่วโมง', 'excluded hours')} ${card.excludedHours.map(h => `${String(h).padStart(2, '0')}:00`).join(', ')} · ` : ''}
        {t('ทุกตัวเลขแสดงพร้อมขนาดตัวอย่าง · เขียว/แดงตัดสินจากช่วงความเชื่อมั่น 95% ไม่ใช่แค่เกิน 50%',
          'every figure carries its sample size · green/red is judged on the 95% confidence interval, not merely above 50%')}
        {card.disclaimer ? ` · ${card.disclaimer}` : ''}
      </p>
    </div>
  );
}
